import { TDrawEntry, TDrawResult } from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import EntryLine from 'components/EntryLine';
import DrawInProgressMessage from 'components/results/DrawInProgressMessage';
import ToggleDrawCode from 'components/results/ToggleDrawCode';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { FRIDAY, MainDrawScheduleCode, TUESDAY } from 'constants/Draw';
import { useUserStore } from 'hooks/stores/useUserStore';
import $ from 'jquery';
import { observer } from 'mobx-react-lite';
import { useEffect, useReducer, useState } from 'react';
import { stringFromDate } from 'utils/common';
import { ASSET_PREFIX, colors } from 'utils/constants';
import { CongratsSection } from './sections/CongratsSection';
import { LoginSection } from './sections/RevealLoginSection';
import { UserEntriesSection } from './sections/UserEntriesSection';
import { exhaustiveCheck } from '@daymade/common-lib/types';

export const ENTRY_LINE_HEIGHT = 110;
export const PRIZE_MESSAGE_HEIGHT = 40;

const ActionType = {
  Start: 'Start',
  Advance: 'Advance',
  Skip: 'Skip',
  Reset: 'Reset',
} as const;

interface RevealAnimationProps {
  entries: TDrawEntry[];
  drawResult: TDrawResult;
  drawInProgress?: boolean;
  loading?: boolean;
  usingBonusBall: boolean;
  handleToggleDrawCode: () => Promise<void>;
}

interface ReducerState {
  revealInProgress: boolean;
  revealStage: number;
  finalStage: number;
  revealStagePart: 'a'|'b'|'c';
  readyToShowFreemiumPopup: boolean;
}

const reducer = (
  state: ReducerState,
  action: { type: typeof ActionType[keyof typeof ActionType] }
): ReducerState => {
  switch (action.type) {
    case ActionType.Start:
      return {
        ...state,
        revealInProgress: true,
      };

    case ActionType.Advance:
      if (state.revealInProgress && state.revealStage < state.finalStage) {
        if (state.revealStage === 0) {
          // Don't perform parts b and c if we're in stage 0 as no numbers have been revealed yet.
          return {
            ...state,
            revealStage: state.revealStage + 1,
            revealStagePart: 'a',
            revealInProgress: true,
          };
        }

        switch (state.revealStagePart) {
          case 'a':
            return {
              ...state,
              revealStagePart: 'b',
            };

          case 'b':
            return {
              ...state,
              revealStagePart: 'c',
            };

          default:
            return {
              ...state,
              revealStage: state.revealStage + 1,
              revealStagePart: 'a',
            };
        }
      } else {
        return {
          ...state,
          revealStage: state.finalStage,
          revealStagePart: 'c',
          revealInProgress: false,
          readyToShowFreemiumPopup: true,
        };
      }

    case ActionType.Skip:
      return {
        ...state,
        revealStage: state.finalStage,
        revealStagePart: 'c',
        revealInProgress: false,
        readyToShowFreemiumPopup: true,
      };

    case ActionType.Reset:
      return {
        ...state,
        revealInProgress: true,
        revealStage: 0,
        revealStagePart: 'a',
        readyToShowFreemiumPopup: false,
      };

    default:
      exhaustiveCheck(action.type);
      throw new Error(`Don't know what to do with Action Type: ${action}`);
  }
};

const RevealAnimation = observer(
  ({
    entries,
    drawResult,
    drawInProgress,
    loading = false,
    usingBonusBall,
    handleToggleDrawCode,
  }: RevealAnimationProps) => {
    const result = drawResult?.draw.result;
    if (
      !result ||
      result?.type != GameType.LOTTERY ||
      result?.main?.length <= 0
    ) {
      return (
        <div
          className="flexCentered fullWidth"
          style={{ margin: '3em 0 5em' }}
        >
          <p className="bold">Draw in progress! Watch this space 👀</p>
        </div>
      );
    }
    const winningLine = result.main.concat(result.bonus);
    const drawCode = drawResult.drawSchedule.code as MainDrawScheduleCode;

    const { currentUser, activeSubscriptions } = useUserStore();

    const [state, dispatch] = useReducer(reducer, {
      revealInProgress: false,
      revealStage:
        currentUser && (!entries || !result || loading || drawInProgress)
          ? 0
          : winningLine.length,
      revealStagePart: 'a',
      finalStage: result && !drawInProgress ? winningLine.length : 0,
      readyToShowFreemiumPopup: false,
    });

    const {
      revealInProgress,
      revealStage,
      revealStagePart,
      finalStage,
      readyToShowFreemiumPopup,
    } = state;

    const [revealStarted, setRevealStarted] = useState(false);
    const [prizeWins, setPrizeWins] = useState<TDrawEntry[]>([]);
    const [materialPrizeWins, setMaterialPrizeWins] = useState<
      TDrawEntry[]
    >([]);
    const [tokensWon, setTokensWon] = useState(0);
    const [largestHypotheticalPrizeWin, setLargestHypotheticalPrizeWin] =
      useState<TDrawEntry | null>(null);
    const [eligibleToSeeFreemiumPopup, setEligibleToSeeFreemiumPopup] =
      useState(false);
    const [showFreemiumPopup, setShowFreemiumPopup] = useState(false);

    const skipReveal = () => {
      dispatch({ type: ActionType.Skip });
    };

    const resetReveal = () => {
      setRevealStarted(false);
      dispatch({ type: ActionType.Reset });
    };

    useEffect(() => {
      if (!result && process.browser) {
        return;
      }

      if (revealStarted && revealInProgress) {
        setTimeout(() => {
          dispatch({ type: ActionType.Advance });
        }, 350);
      }
    }, [revealInProgress, revealStage, revealStagePart]);

    useEffect(() => {
      if (entries?.length) {
        sortEntries();
      }
    }, [entries?.length, state]);

    useEffect(() => {
      if (entries?.length > 0) {
        const wins = entries.filter(
          (entry) => !entry.isHypotheticalPrize && entry.prizeTierCode
        );
        setPrizeWins(wins);
        setMaterialPrizeWins(
          wins.filter(
            (entry) =>
              !['TH_TOKEN', 'TH_TOKEN_X2'].includes(
                entry.prizeTierCode as string
              )
          )
        );
        setTokensWon(
          prizeWins.filter((entry) => entry.prizeTierCode === 'TH_TOKEN')
            .length +
            prizeWins.filter((entry) => entry.prizeTierCode === 'TH_TOKEN_X2')
              .length *
              2
        );

        const hypotheticalPrizeWins = entries.filter(
          (entry) => entry.isHypotheticalPrize
        );
        const largestHPW =
          hypotheticalPrizeWins.length > 0 ? hypotheticalPrizeWins[0] : null;
        setLargestHypotheticalPrizeWin(largestHPW);

        const activeSubscriptionCodes = activeSubscriptions.map(
          (actSub) => actSub.drawSchedulePlans[0].drawTypePlan.code
        );
        const isFreemiumOrLiteUser = !!activeSubscriptionCodes.some((code) =>
          ['LITE', 'FREEMIUM'].includes(code)
        );

        setEligibleToSeeFreemiumPopup(isFreemiumOrLiteUser && !!largestHPW);

        if (!revealInProgress) {
          // Start the reveal.
          setTimeout(() => dispatch({ type: ActionType.Start }), 200);
        }
      } else if (entries?.length === 0) {
        setPrizeWins([]);
        setMaterialPrizeWins([]);
        setTokensWon(0);
        setLargestHypotheticalPrizeWin(null);
        setEligibleToSeeFreemiumPopup(false);
      }
    }, [entries]);

    useEffect(() => {
      // The componentWillUnmount effect needs to be separate as the above hook is called on every
      // revealInProgress change. This was causing this cleanup function to be called on every reveal.
      return function cleanup() {
        dispatch({ type: ActionType.Skip });
      };
    }, []);

    useEffect(() => {
      if (readyToShowFreemiumPopup && eligibleToSeeFreemiumPopup) {
        setTimeout(() => {
          setShowFreemiumPopup(true);
        }, 1500);
      }
    }, [readyToShowFreemiumPopup]);

    // Calculates the score for a line (higher scoring lines correspond to higher value prizes).
    // Score is only calculated up to the specified index (so we sort incrementally).
    const scoreForLineToIndex = (line: any[], toIndex: number) => {
      let bonusBallMatch = false;
      if (usingBonusBall) {
        // Don't compare bonus ball with main numbers (instead, we compare that later).
        toIndex = Math.min(toIndex, line.length - 2);
        if (
          toIndex + 1 === line.length - 1 &&
          line[line.length - 1] === winningLine[winningLine.length - 1]
        ) {
          bonusBallMatch = true;
        }
      }

      let score = 0;

      line.slice(0, toIndex + 1).forEach((number: any) => {
        if (winningLine.slice(0, toIndex + 1).includes(number)) {
          score += 2; // main number match = 2 points.
        }
      });

      score += bonusBallMatch ? 1 : 0; // bonus ball match = 1 point.

      return score;
    };

    const sortEntries = () => {
      const sortedEntries = entries
        .concat()
        .sort(
          (line1: { numbers: any }, line2: { numbers: any }) =>
            scoreForLineToIndex(line2.numbers.main, revealStage - 1) -
            scoreForLineToIndex(line1.numbers.main, revealStage - 1)
        );

      let yPosition = 0;

      for (let i = 0; i < sortedEntries.length; i++) {
        $(`#entryLine_${sortedEntries[i].id}`).css('top', yPosition);
        yPosition += ENTRY_LINE_HEIGHT;
        if (
          revealStage === finalStage &&
          sortedEntries[i].prizeTierCode &&
          !sortedEntries[i].isHypotheticalPrize
        ) {
          yPosition += PRIZE_MESSAGE_HEIGHT;
        }
      }
    };

    return (
      <div className="revealAnimation fullWidth flexCentered">
        <div className="thisWeeksWinningNumbers fullWidth flexCentered">
          <div className="sectionHeading fullWidth flexCentered">
            <img
              src={`${ASSET_PREFIX}/images/results/trophy-icon.svg`}
              className="icon"
            />
            <h3 style={{ marginBottom: '0.3em' }}>Latest winning numbers</h3>
            {handleToggleDrawCode && (
              <ToggleDrawCode
                selectedDrawCode={drawCode}
                handleToggle={async () => {
                  await handleToggleDrawCode();
                  if (
                    entries?.length > 0 &&
                    (revealInProgress || revealStage === finalStage)
                  ) {
                    resetReveal();
                  }
                }}
              />
            )}

            {drawResult.drawSchedule.code === TUESDAY && (
              <p
                style={{
                  marginTop: '1em',
                  fontSize: '1em',
                  textAlign: 'center',
                  maxWidth: '320px',
                }}
              >
                <em>
                  Tuesday draws are carried out using an independent{' '}
                  <a
                    href="https://www.random.org/"
                    target="_blank"
                  >
                    3rd party
                  </a>
                </em>
              </p>
            )}

            {drawResult.drawSchedule.code === FRIDAY && (
              <p
                style={{
                  marginTop: '1em',
                  fontSize: '1em',
                  textAlign: 'center',
                }}
              >
                <em>
                  Friday draws take place live on{' '}
                  <a
                    href="https://www.instagram.com/daymadeuk/"
                    target="_blank"
                  >
                    Instagram
                  </a>
                </em>
              </p>
            )}

            {!revealStarted && currentUser && !loading && !drawInProgress && (
              <div className="fullWidth flexCentered desktopOnly">
                <RoundedButton
                  style={{ marginTop: '1em' }}
                  onClick={() => {
                    setRevealStarted(true);
                    dispatch({ type: ActionType.Advance });
                  }}
                >
                  Reveal now
                </RoundedButton>
              </div>
            )}

            {revealStarted && revealInProgress && (
              <div
                className={`skipRevealButton${
                  revealInProgress ? '' : ' hidden'
                }`}
                onClick={skipReveal}
              >
                Skip reveal
              </div>
            )}
          </div>

          <div className="fullWidth flexCentered">
            {drawInProgress ||
            drawResult.draw.result?.type != GameType.LOTTERY ? (
              <DrawInProgressMessage drawCode={drawCode} />
            ) : (
              <div className="winning linesContainer">
                <EntryLine
                  main={drawResult.draw.result?.main}
                  bonus={drawResult.draw.result?.bonus}
                  drawDate={
                    /**
                     * @todo: Remove 'new Date()' when Nextjs is upgraded to 13
                     * Adding superjson to be used getting api data in serverside props
                     */
                    drawResult?.draw.drawDate
                      ? stringFromDate(new Date(drawResult.draw.drawDate))
                      : undefined
                  }
                  mainNumbersProofUrl={
                    drawResult.draw.result?.mainAdditionalInfo
                      ?.verificationPageLink
                  }
                  bonusNumberProofUrl={
                    drawResult.draw.result?.bonusAdditionalInfo
                      ?.verificationPageLink
                  }
                  revealStage={loading ? 0 : revealStage}
                  style={{ maxWidth: '500px' }}
                  skipFlash={!currentUser}
                  usingBonusBall={usingBonusBall}
                />
              </div>
            )}
          </div>

          {!revealStarted && currentUser && (
            <div className="fullWidth flexCentered mobileOnly">
              <RoundedButton
                style={{ marginTop: '3em' }}
                onClick={() => {
                  setRevealStarted(true);
                  dispatch({ type: ActionType.Advance });
                }}
              >
                Reveal now
              </RoundedButton>
            </div>
          )}
        </div>

        {!drawInProgress && (
          <>
            <Separator style={{ margin: '3em 0 1em' }} />

            <div className="userEntriesForThisDraw fullWidth flexCentered">
              <div className="sectionHeading fullWidth flexCentered mobileOnly">
                <img
                  src={`${ASSET_PREFIX}/images/results/ticket-icon.svg`}
                  className="icon"
                />
                <h3>Your entries for this draw</h3>
              </div>

              {revealInProgress || prizeWins.length === 0 ? (
                <div className="sectionHeading fullWidth flexCentered desktopOnly">
                  <img
                    src={`${ASSET_PREFIX}/images/results/ticket-icon.svg`}
                    className="icon"
                  />
                  <h3>Your entries for this draw</h3>
                </div>
              ) : (
                <CongratsSection
                  additionalClassName="desktopOnly"
                  revealInProgress={revealInProgress}
                  prizeWins={prizeWins}
                  materialPrizeWins={materialPrizeWins}
                  tokensWon={tokensWon}
                />
              )}

              {currentUser ? (
                <UserEntriesSection
                  entries={entries}
                  largestHypotheticalPrizeWin={largestHypotheticalPrizeWin}
                  prizeWins={prizeWins}
                  result={result}
                  revealInProgress={revealInProgress}
                  revealStage={revealStage}
                  revealStagePart={revealStagePart}
                  revealStarted={revealStarted}
                  setShowFreemiumPopup={setShowFreemiumPopup}
                  showFreemiumPopup={showFreemiumPopup}
                />
              ) : (
                <LoginSection />
              )}
            </div>
          </>
        )}

        <CongratsSection
          additionalClassName="mobileOnly"
          revealInProgress={revealInProgress}
          prizeWins={prizeWins}
          materialPrizeWins={materialPrizeWins}
          tokensWon={tokensWon}
        />

        <style jsx>{`
          .sectionHeading {
            margin: 2em 0;
          }

          .icon {
            height: 25px;
          }

          h3,
          h4 {
            text-transform: none;
            letter-spacing: 0;
          }

          a {
            text-decoration: underline;
          }

          .sectionContainer {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            background: url('${ASSET_PREFIX}/images/landing/waves.svg');
          }

          .loginSection {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          :global(.loginText) {
            font-size: 1.2em;
            text-align: center;
          }

          :global(.loginSection .loginButton) {
            color: white;
            background-color: ${colors.primaryBrandGray};
            margin-top: 20px;
            width: 100%;
          }

          :global(.loginSection .loginButton:hover) {
            background-color: ${colors.primaryBrandGrayPressed};
          }

          :global(.darkBackgroundContainer) {
            display: flex;
            flex-direction: column;
            align-items: center;
            overflow: visible;
            background: #726e96;
            width: 100%;
            padding-top: 20px;
            padding-bottom: 20px;
            margin-top: 15px;
            margin-bottom: 20px;
          }

          :global(
              .darkBackgroundContainer .paddedMaxWidthContainer h1,
              .darkBackgroundContainer .paddedMaxWidthContainer h2
            ) {
            color: white;
          }

          :global(.skipRevealButton) {
            cursor: pointer;
            font-weight: bold;
            margin: 0.5em 0 0;
          }

          :global(.skipRevealButton.hidden) {
            opacity: 0;
            transition: 0.2s;
          }

          :global(.linesContainer) {
            width: 100%;
            position: relative;
            max-width: 700px;
          }

          :global(.trophyLineContainer) {
            position: absolute;
            width: 100%;
            transition: 0.2s;
          }

          :global(.trophyLineContainer .ui.purple.message) {
            background-color: #fcf7ff;
            color: #25004b;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            margin: 16px auto auto auto;
            width: 80%;
            box-shadow: 1px 5px 18px rgba(0, 0, 0, 0.3);
            border-radius: 0px 0px 7px 7px;
            border: 2px solid #aaa;
            padding: 12px;
          }

          :global(.prizeIcon) {
            width: 30px;
            height: 30px;
            font-size: 1.7em;
            margin-right: 10px;
          }

          :global(.youWonMessage) {
            display: flex;
            flex-direction: column;
            align-items: center;
            position: absolute;
            text-align: center;
            margin-right: 10px;
            width: 100%;
            top: ${ENTRY_LINE_HEIGHT}px;
            text-transform: uppercase;
          }

          :global(.footer) {
            margin-top: 0 !important;
          }

          :global(div.mobileOnly) {
            display: flex;
          }

          :global(div.desktopOnly) {
            display: none;
          }

          :global(.revealAnimation .roundedButton) {
            width: 90%;
            max-width: 250px;
          }

          :global(.revealAnimation .thisWeeksWinningNumbers .toggleDrawCode) {
            margin: 1em 0;
          }

          @media (min-width: 900px) {
            .sectionHeading {
              margin: 1em 0;
            }

            .thisWeeksWinningNumbers,
            .userEntriesForThisDraw {
              flex-direction: row;
              justify-content: space-between;
              align-items: flex-start;
              padding: 1.5em 0;
            }

            :global(div.mobileOnly) {
              display: none;
            }

            :global(div.desktopOnly) {
              display: flex;
            }
          }
        `}</style>
      </div>
    );
  }
);

export default RevealAnimation;
